import { SERVICES_URL as servicesUrl, handleError } from "api/helper";
import { del, post, get } from "api/http";
import { BillingService } from "@adfenix/types";

const SERVICES_URL = servicesUrl;

const baseAuthed = "billing/auth/v1";

export interface BillingCycle {
  year: number;
  month: number;
}

export const getPaymentStatus = (
  status: BillingService.BillingServiceSharedModelsV1EnumsPaymentStatus
) => {
  switch (status) {
    case "unknown":
      return "Unknown";
    case "credited":
      return "Credited";
    case "doNotInvoice":
      return "Do not invoice";
    case "invoiceCreated":
      return "Invoice created";
    case "paidByCreditCard":
      return "Paid by credit card";
    case "usageRegistered":
      return "Usage registered";
    default:
      return "Unknown";
  }
};

export const creditCampaign = ({
  campaignId,
  model
}: {
  campaignId: string;
  model: BillingService.CreditedRequest;
}): Promise<void> =>
  post(`${SERVICES_URL}/${baseAuthed}/${campaignId}/credit`, model).catch(
    handleError
  );

export const removeCredited = ({
  campaignId
}: {
  campaignId: string;
}): Promise<void> =>
  del(`${SERVICES_URL}/${baseAuthed}/${campaignId}/remove-credit`).catch(
    handleError
  );

export const getInvoiceReport = (invoiceId: string): Promise<string> =>
  get(`${SERVICES_URL}/${baseAuthed}/get/invoice/${invoiceId}`).catch(
    handleError
  );

export const getZeroSpendReport = (
  billingCycle: BillingCycle
): Promise<string> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/report/zero-spend/${billingCycle.year}/${billingCycle.month}`
  ).catch(handleError);

export const getBillingReport = (billingCycle: BillingCycle): Promise<string> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/report/billing/${billingCycle.year}${String(
      "0" + billingCycle.month
    ).slice(-2)}`
  ).catch(handleError);

export const getCreditedCampaignsReport = (
  billingCycle: BillingCycle
): Promise<string> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/report/credited-campaigns/${billingCycle.year}/${billingCycle.month}`
  ).catch(handleError);

export const getOfficeInvoiceInfo = (
  id: string
): Promise<BillingService.CustomerResponse> =>
  get(`${SERVICES_URL}/${baseAuthed}/customer/${id}`).catch(handleError);

export const getOfficeInvoiceHistory = (
  officeId: string
): Promise<BillingService.CustomerInvoice[]> =>
  get(`${SERVICES_URL}/${baseAuthed}/invoice/office/${officeId}`).catch(
    handleError
  );

export const getHqInvoiceInfo = (
  id: string
): Promise<BillingService.CustomerResponse> =>
  get(`${SERVICES_URL}/${baseAuthed}/customer/${id}`).catch(handleError);

export const getHqInvoiceHistory = (
  hqId: string
): Promise<BillingService.CustomerInvoice[]> =>
  get(`${SERVICES_URL}/${baseAuthed}/invoice/hq/${hqId}`).catch(handleError);
export const getCampaignFinancialInfo = (
  campaignId: string
): Promise<BillingService.CampaignFinancialInfo> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/campaign/financial-info/${campaignId}`
  ).catch(handleError);

export const getDniedCampaignsReport = (
  billingCycle: BillingCycle
): Promise<string> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/report/dnied-campaigns/${billingCycle.year}/${billingCycle.month}`
  ).catch(handleError);

export const generateInvoiceDetailsReports = (
  chargifySite: string,
  billingDate: string
): Promise<void> =>
  post(`${SERVICES_URL}/${baseAuthed}/create/invoices-detail-reports`, {
    chargifySite,
    billingDate
  }).catch(handleError);

export const sendInvoiceDetailsReports = (
  chargifySite: string,
  billingDate: string
): Promise<void> => {
  return post(`${SERVICES_URL}/${baseAuthed}/send/invoices-detail-reports`, {
    chargifySite,
    billingDate
  }).catch(handleError);
};

export const getBillingPeriod =
  (): Promise<BillingService.CreateBillingPeriodRequest> =>
    post(`${SERVICES_URL}/${baseAuthed}/billing-period/get-latest`).catch(
      handleError
    );
export const updateBillingPeriod = (
  billingPeriod: BillingService.CreateBillingPeriodRequest
): Promise<void> =>
  post(`${SERVICES_URL}/${baseAuthed}/billing-period/add`, billingPeriod).catch(
    handleError
  );

export const getPaymentDetailsForOrder = (
  orderId: string
): Promise<BillingService.PaymentDetailsModel[]> =>
  get(`${SERVICES_URL}/${baseAuthed}/payment/get-payments/${orderId}`).catch(
    handleError
  );

export const setContractSigned = (
  request: BillingService.SetContractSignedRequest
): Promise<void> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/payment/set-contract-signed`,
    request
  ).catch(handleError);
